    import dayjs, {Dayjs} from 'dayjs';
    import React, {useState} from 'react';
    import {Popover, Transition} from '@headlessui/react';
    import {useQuery, useTheme, useTranslations} from 'react-ui';
    import {Calendar} from 'react-ui';
    import FilterIcon from "../../assets/filter-icon.svg?react"
    import {FilterLabelCarousel} from "./FilterLabelCarousel.tsx";
    import {useMapBoundingBox} from "../contexts/MapBoundingBox.tsx";
    import {CloseButton} from "./CloseButton.tsx";
    import {CloseMobileDialogFooterButton} from "./CloseMobileDialogFooterButton.tsx";
    export function FastFilterButton({
        dates,
        selectedDates,
        setSelectedDates,
        label,
    }: {
        dates: Dayjs[];
        selectedDates: Dayjs[];
        setSelectedDates: (dates: Dayjs[]) => void;
        label: string;
    }) {
        const {primaryColor} = useTheme();

        function isSelected() {
            if (selectedDates.length !== dates.length) return false;
            for (let i = 0; i < dates.length; i++) {
                if (!dates[i].isSame(selectedDates[i], 'day')) return false;
            }
            return true;
        }

        return (
            <button
                style={{
                    backgroundColor: isSelected() ? primaryColor : 'white',
                    color: isSelected() ? 'white' : 'black',
                    padding: '6px 16px',
                }}
                className="shadow rounded-full text-sm font-medium transition-colors duration-200 ease-in-out"
                onClick={
                    isSelected()
                        ? () => setSelectedDates([])
                        : () => setSelectedDates(dates)
                }
            >
                {label}
            </button>
        );
    }

    export function FilterPicker({
        selectedDates,
        setSelectedDates,
        highlight,
        embedded = false,
    }: {
        selectedDates: Dayjs[];
        setSelectedDates: (dates: Dayjs[]) => void;
        highlight: boolean;
        embedded?: boolean;
    }) {
        const {primaryColor} = useTheme();
        const {width, height, top, left} = useMapBoundingBox()
        const {translated} = useTranslations();
        const { removeQueryById, has, queries } = useQuery();
        const clearDates = () => {
            setSelectedDates([]);
            removeQueryById('dates');
        }
        function onClick(clickedDate: Dayjs) {
            let base = selectedDates;
            if (base.length >= 2) {
                base = [];
            }
            if (
                selectedDates.length === 1 &&
                selectedDates[0].isSame(clickedDate, 'day')
            ) {
                setSelectedDates([]);
            } else {
                setSelectedDates(
                    [...base, clickedDate].sort((a, b) => a.unix() - b.unix())
                );
            }
        }
        return (
            <Popover className="filterpicker-popover relative">
                <Popover.Button
                    className={`filterpicker-button z-1 p-2 flex items-center justify-center relative ${
                        embedded
                            ? 'rounded-full hover:bg-gray-100'
                            : `bg-white shadow-lg rounded-full mx-0 mt-2 ${highlight ? 'outline outline-2' : ''}`
                    }`}
                    style={{outlineColor: !embedded && highlight ? primaryColor : 'transparent'}}
                >
                    <FilterIcon style={embedded && highlight ? {color: primaryColor} : undefined}/>
                    {(has('dates') || has('labels')) ? (
                        <span
                            className="absolute bottom-[-2px] right-[-2px] flex items-center justify-center w-3 h-3 text-xs font-bold text-white rounded-full"
                            style={{backgroundColor: '#AC2475'}}
                        ></span>
                    ) : null}
                </Popover.Button>
                <Transition enter="transition ease-out duration-200" leave={"transition ease-out duration-200"} leaveFrom={"translate-y-0"} leaveTo={"translate-y-[-100%]"} enterFrom={"translate-y-[-100%]"} enterTo={"translate-y-0"}>
                <Popover.Panel
                    className="filterpicker-panel fixed z-10"
                    style={{top: `${top}px`, left: `${left}px`, width: `${width}px`, height: `${height}px`, overflow: 'hidden'}}>
                    {({close}) => (
                        <div
                            className="flex flex-col relative items-center justify-center py-0 w-full h-full bg-white rounded-lg shadow-lg ">

                            <div className="w-full h-full flex flex-col">
                                <div className={"filterpicker-header flex-shrink-0 flex flex-row w-full items-center justify-between px-4 py-2  border-b border-gray-200"}>
                                    <div className={"flex flex-1 flex-row items-center justify-start px-2 gap-2"}>
                                        <FilterIcon/>
                                        <h2 className={"text-gray-500 text-xl font-medium font-circular leading-tight text-center uppercase"} style={{fontWeight: 500}}>{translated('filters')}</h2>
                                    </div>
                                    <CloseButton onClick={close}/>
                                </div>
                                <div style={{maxHeight: 'calc(100vh - 167px)'}} className="filterpicker-content overflow-y-auto flex-1">

                                    <div className="md:px-8 md:pb-8 p-5 bg-white rounded-lg flex flex-col pt-0">
                                        <h3 className={"text-gray-500 text-2xl font-medium font-circular leading-tight text-center my-4 uppercase"} style={{fontWeight: 500}}>{translated('categories')}</h3>
                                        <FilterLabelCarousel className={"w-full grid grid-cols-3 gap-1 px-2 py-2 flex-wrap [&_.badge-content]:w-full"}/>
                                        <h3 className={"text-gray-500 text-2xl font-medium font-circular leading-tight text-center my-4 uppercase"} style={{fontWeight: 500}}>{translated('dates')}</h3>
                                        <div className={`flex w-full justify-center mb-4 gap-4`}>
                                            <FastFilterButton
                                                dates={[dayjs()]}
                                                label={translated('calendar_today')}
                                                selectedDates={selectedDates}
                                                setSelectedDates={setSelectedDates}
                                            />
                                            <FastFilterButton
                                                dates={[dayjs(), dayjs().add(7, 'days')]}
                                                label={`7 ${translated('calendar_days')}`}
                                                selectedDates={selectedDates}
                                                setSelectedDates={setSelectedDates}
                                            />
                                            <FastFilterButton
                                                dates={[dayjs(), dayjs().add(30, 'days')]}
                                                label={`30 ${translated('calendar_days')}`}
                                                selectedDates={selectedDates}
                                                setSelectedDates={setSelectedDates}
                                            />
                                            <FastFilterButton
                                                dates={[dayjs(), dayjs().add(90, 'days')]}
                                                label={`90 ${translated('calendar_days')}`}
                                                selectedDates={selectedDates}
                                                setSelectedDates={setSelectedDates}
                                            />
                                        </div>
                                        <Calendar
                                            selectedDates={[selectedDates.map((d) => d.toDate())]}
                                            onClickDate={(d) => onClick(dayjs(d))}
                                        />
                                        <div className={"flex flex-row w-full items-center justify-center px-2 py-2"}>
                                            <button
                                                disabled={!has('dates')}
                                                className={"self-center w-full inline-flex mt-3 justify-center rounded-full border border-transparent px-4 py-2 text-sm font-medium text-[#F4E3F7] bg-[#BF2088] hover:text-[#BF2088] hover:bg-[#F4E3F7] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#BF2088] focus-visible:ring-offset-2"}
                                                onClick={clearDates}>{translated('clear')}</button>
                                        </div>

                                    </div>

                                </div>
                            </div>
                            <div className="filterpicker-footer flex flex-row justify-center items-center  px-4 py-2 w-full border-t border-gray-200">
                                <CloseMobileDialogFooterButton onClick={close} label={'ok'}/>
                            </div>
                        </div>
                    )}
                </Popover.Panel>
                </Transition>
            </Popover>
        );
    }
